import React, { useState, useEffect } from 'react';
import { getAllTestimonials, createTestimonial, updateTestimonial, deleteTestimonial, uploadImage } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, Pencil, Trash2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import AdminLayout from '../components/AdminLayout';

// Reusable image component with error handling
const TestimonialImage = ({ src, alt, className, fallbackClassName }) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'

  useEffect(() => {
    if (!src) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    
    const img = new Image();
    img.onload = () => setStatus('loaded');
    img.onerror = () => setStatus('error');
    img.src = src;
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  // Show fallback for error or no source
  if (status === 'error' || !src) {
    return (
      <div className={fallbackClassName || "w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0"}>
        <span className="text-white font-bold text-lg">{alt?.charAt(0)?.toUpperCase() || '?'}</span>
      </div>
    );
  }

  // Show loading state
  if (status === 'loading') {
    return (
      <div className={fallbackClassName || "w-16 h-16 bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse"}>
        <ImageIcon className="w-6 h-6 text-slate-500" />
      </div>
    );
  }

  // Show loaded image
  return (
    <img
      src={src}
      alt={alt || ''}
      className={className}
    />
  );
};

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [formData, setFormData] = useState({
    founder: '',
    company: '',
    title: '',
    quote: '',
    category: '',
    linkedin_url: '',
    funding_info: '',
    image_url: '',
    featured: false,
    active: true,
    order: 0
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const data = await getAllTestimonials();
      setTestimonials(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateTestimonial(editing, formData);
      } else {
        await createTestimonial(formData);
      }
      loadTestimonials();
      resetForm();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      alert('Error saving testimonial');
    }
  };

  const handleEdit = (testimonial) => {
    setEditing(testimonial._id);
    setFormData({
      founder: testimonial.founder,
      company: testimonial.company,
      title: testimonial.title,
      quote: testimonial.quote,
      category: testimonial.category,
      linkedin_url: testimonial.linkedin_url || '',
      funding_info: testimonial.funding_info || '',
      image_url: testimonial.image_url || '',
      featured: testimonial.featured,
      active: testimonial.active,
      order: testimonial.order
    });
    setImagePreview(testimonial.image_url || null);
    setImageError(false);
    setShowForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP images.');
      return;
    }

    // Show local preview immediately using FileReader
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setImageError(false);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const result = await uploadImage(file);
      // Use URL directly - Cloudinary returns full URL, local uploads return relative path
      const imageUrl = result.url.startsWith('http') 
        ? result.url 
        : `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      // Keep showing the local preview (FileReader result) since it's already working
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
      setImagePreview(formData.image_url || null);
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setFormData({ ...formData, image_url: '' });
    setImagePreview(null);
    setImageError(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        await deleteTestimonial(id);
        loadTestimonials();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        alert('Error deleting testimonial');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      founder: '',
      company: '',
      title: '',
      quote: '',
      category: '',
      linkedin_url: '',
      funding_info: '',
      image_url: '',
      featured: false,
      active: true,
      order: 0
    });
    setEditing(null);
    setShowForm(false);
    setImagePreview(null);
    setImageError(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-white p-8">Loading...</div>;
  }

  return (
    <AdminLayout currentPage="testimonials">
      {/* Add Button */}
      <div className="flex justify-end mb-6">
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Testimonial
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="mb-8 bg-slate-900 border-slate-800">
          <CardHeader>
              <CardTitle className="text-white">
                {editing ? 'Edit Testimonial' : 'Add New Testimonial'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Founder Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.founder}
                      onChange={(e) => setFormData({ ...formData, founder: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Company *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Category *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Quote *
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.quote}
                    onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      LinkedIn URL
                    </label>
                    <input
                      type="url"
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Funding Info
                    </label>
                    <input
                      type="text"
                      value={formData.funding_info}
                      onChange={(e) => setFormData({ ...formData, funding_info: e.target.value })}
                      placeholder="e.g., Backed by a16z, Lightspeed"
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Founder Image
                  </label>
                  <div className="flex gap-4">
                    {/* Image Preview */}
                    <div className="flex-shrink-0">
                      {imagePreview && !imageError ? (
                        <div className="relative">
                          <img
                            src={imagePreview}
                            alt=""
                            className="w-24 h-24 object-cover rounded-lg border border-slate-700"
                            onError={() => setImageError(true)}
                            onLoad={() => setImageError(false)}
                          />
                          <button
                            type="button"
                            onClick={clearImage}
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-24 h-24 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center relative">
                          <ImageIcon className="w-8 h-8 text-slate-500" />
                          {formData.image_url && (
                            <button
                              type="button"
                              onClick={clearImage}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Upload & URL inputs */}
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                          <div className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer transition-colors">
                            <Upload className="w-4 h-4" />
                            {uploading ? 'Uploading...' : 'Upload Image'}
                          </div>
                        </label>
                      </div>
                      <div className="text-xs text-slate-500">Or paste image URL below:</div>
                      <input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => {
                          setFormData({ ...formData, image_url: e.target.value });
                          setImagePreview(e.target.value || null);
                          setImageError(false);
                        }}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-slate-300">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Featured (Hero Section)
                  </label>
                  <label className="flex items-center gap-2 text-slate-300">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Active
                  </label>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editing ? 'Update' : 'Create'} Testimonial
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* List */}
        <div className="grid gap-4">
          {testimonials.map((testimonial) => (
            <Card key={testimonial._id} className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex justify-between items-start gap-4">
                  {/* Image thumbnail with error handling */}
                  <TestimonialImage
                    src={testimonial.image_url}
                    alt={testimonial.founder}
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    fallbackClassName="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-white">{testimonial.founder}</h3>
                      {testimonial.featured && (
                        <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30">Featured</Badge>
                      )}
                      {!testimonial.active && (
                        <Badge className="bg-red-500/10 text-red-400 border-red-500/30">Inactive</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mb-3">
                      {testimonial.title} at {testimonial.company} • {testimonial.category}
                    </p>
                    <p className="text-slate-300 mb-2">"{testimonial.quote}"</p>
                    {testimonial.funding_info && (
                      <p className="text-xs text-blue-400 mb-2">{testimonial.funding_info}</p>
                    )}
                    {testimonial.linkedin_url && (
                      <a href={testimonial.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline">
                        View on LinkedIn →
                      </a>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(testimonial)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(testimonial._id)}
                      className="border-red-800 text-red-400 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
    </AdminLayout>
  );
};

export default AdminTestimonials;
